/** Keep in sync with backend `email-template-import.constants.ts` where noted. */
export const FOLDER_IMPORT_MAX_FILES = 2000;
export const FOLDER_IMPORT_MAX_FILE_BYTES = 50 * 1024 * 1024; // 50 MB
/** Soft warning threshold — chunked upload helps, but very large folders still take time. */
export const FOLDER_IMPORT_WARN_TOTAL_BYTES = 500 * 1024 * 1024; // 500 MB

/** Per-request batch limits for chunked folder import (keep small to avoid proxy/API timeouts). */
export const FOLDER_IMPORT_BATCH_MAX_FILES = 20;
export const FOLDER_IMPORT_BATCH_MAX_BYTES = 40 * 1024 * 1024; // 40 MB

export type FolderImportPreflightResult =
  | { ok: true; totalBytes: number; fileCount: number; warnLarge?: string }
  | { ok: false; message: string };

export function formatImportBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Validate folder contents before upload so users get clear errors
 * instead of hanging near 96% on the server.
 */
export function validateFolderImportFiles(files: File[]): FolderImportPreflightResult {
  if (!files.length) {
    return { ok: false, message: 'Folder contains no files to import' };
  }

  if (files.length > FOLDER_IMPORT_MAX_FILES) {
    return {
      ok: false,
      message: `Too many files (${files.length}). Import supports at most ${FOLDER_IMPORT_MAX_FILES} files. Split the folder and try again.`,
    };
  }

  const oversized = files.filter((file) => file.size > FOLDER_IMPORT_MAX_FILE_BYTES);
  if (oversized.length > 0) {
    const sample = oversized
      .slice(0, 3)
      .map((file) => `${file.name} (${formatImportBytes(file.size)})`)
      .join(', ');
    const more = oversized.length > 3 ? ` and ${oversized.length - 3} more` : '';
    return {
      ok: false,
      message: `One or more files exceed the ${formatImportBytes(FOLDER_IMPORT_MAX_FILE_BYTES)} limit: ${sample}${more}. Remove or split those files.`,
    };
  }

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const batchHint = `Files are uploaded in batches of up to ${FOLDER_IMPORT_BATCH_MAX_FILES} files / ~${formatImportBytes(FOLDER_IMPORT_BATCH_MAX_BYTES)}.`;
  const warnLarge =
    totalBytes > FOLDER_IMPORT_WARN_TOTAL_BYTES
      ? `This folder is ${formatImportBytes(totalBytes)} (${files.length} files). ${batchHint} Large imports can take several minutes. Continue?`
      : undefined;

  return { ok: true, totalBytes, fileCount: files.length, warnLarge };
}

/** Human-readable import failure from axios / Nest responses. */
export function folderImportErrorMessage(err: unknown, fallback = 'Failed to import folder'): string {
  const axiosErr = err as {
    code?: string;
    message?: string;
    response?: { status?: number; data?: { message?: string | string[] } };
  };

  if (
    axiosErr.code === 'ECONNABORTED' ||
    axiosErr.code === 'ETIMEDOUT' ||
    /timeout/i.test(axiosErr.message || '')
  ) {
    return 'Import timed out while waiting for the server. Refresh the library — upload may have finished and import may still be running in the background.';
  }

  const msg = axiosErr.response?.data?.message;
  if (Array.isArray(msg) && msg.length > 0) {
    return msg.filter((item): item is string => typeof item === 'string').join('; ') || fallback;
  }
  if (typeof msg === 'string' && msg.trim()) {
    return msg;
  }

  if (axiosErr.response?.status === 413) {
    return 'Upload too large for the server. Split the folder and try again.';
  }

  return fallback;
}
